import { Dispatch } from 'react'
import DuplicateDatePicker from './DuplicateDatePicker'
import TimePicker from '@/Views/FormSteps/DateTime/TimePicker'
import TimingsCaption from '@/Views/FormSteps/DateTime/TimingsCaption'
import useLocale from '@/Hooks/Locale/useLocale'
import { type DateRange } from '@/Views/FormSteps/DateTime/Utilities/getBriefDates'
import { type TimeOptionInput } from '@/Views/FormSteps/DateTime/TimePicker/TimeStamps/timeStamps'
import { ClockIcon } from '@/Components/UI/Icons'

const timePickerStyle = (hasValue: boolean) => ({
  width: '100%',
  borderColor: '#bfbfbf',
  color: hasValue ? '#000000' : '#bfbfbf',
})

interface TimingsSectionProps {
  dates: DateRange
  setDates: Dispatch<React.SetStateAction<DateRange>>
  startTime: TimeOptionInput
  setStartTime: Dispatch<React.SetStateAction<TimeOptionInput>>
  endTime: TimeOptionInput
  setEndTime: Dispatch<React.SetStateAction<TimeOptionInput>>
  isFlexibleOnDate: boolean
  setIsFlexibleOnDate: Dispatch<React.SetStateAction<boolean>>
  isFlexibleOnTime: boolean
  setIsFlexibleOnTime: Dispatch<React.SetStateAction<boolean>>
  flexibilityNotes: string
  setFlexibilityNotes: Dispatch<React.SetStateAction<string>>
  dateError?: boolean
  timeError?: boolean
}

const TimingsSection = ({
  dates,
  setDates,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  isFlexibleOnDate,
  setIsFlexibleOnDate,
  isFlexibleOnTime,
  setIsFlexibleOnTime,
  flexibilityNotes,
  setFlexibilityNotes,
  dateError,
  timeError,
}: TimingsSectionProps) => {
  const locale = useLocale()
  const timeFormat = locale === 'GB' ? '24-hour' : '12-hour'

  return (
    <div className='flex flex-col gap-4'>
      <p className='!font-bold text-sm sm:text-base text-black leading-6'>
        What are your preferred timings? <span className='text-[#af231c]'>*</span>
      </p>
      <div className='flex flex-col gap-4 max-w-[459px]'>
        <DuplicateDatePicker dates={dates} setDates={setDates} hasError={dateError} />
        {dateError && <p className='!text-[#af231c] text-sm'>Please select a date.</p>}

        <label className='flex items-center gap-2 cursor-pointer select-none'>
          <input
            type='checkbox'
            id='flexible-date'
            checked={isFlexibleOnDate}
            onChange={({ target }) => setIsFlexibleOnDate(target.checked)}
            className='w-4 h-4 cursor-pointer accent-black shrink-0'
          />
          <span className='text-base text-black font-normal'>Flexible date</span>
        </label>

        <div className='flex gap-2'>
          <div className='flex-1 flex flex-col gap-2'>
            <label htmlFor='start-time' className='text-sm sm:text-base text-[#1f1f1f] leading-6'>
              Starting time:
            </label>
            <TimePicker
              id='start-time'
              value={startTime}
              setValue={setStartTime}
              format={timeFormat}
              placeholder='Select a time'
              className='block w-full'
              icon={<ClockIcon className='w-4 h-4' />}
              style={timePickerStyle(!!startTime)}
              compact
            />
          </div>
          <div className='flex-1 flex flex-col gap-2'>
            <label htmlFor='end-time' className='text-sm sm:text-base text-[#1f1f1f] leading-6'>
              Finishing time:
            </label>
            <TimePicker
              id='end-time'
              value={endTime}
              setValue={setEndTime}
              baseValue={startTime || undefined}
              format={timeFormat}
              placeholder='Select a time'
              className='block w-full'
              icon={<ClockIcon className='w-4 h-4' />}
              style={timePickerStyle(!!endTime)}
              compact
            />
          </div>
        </div>
        {timeError && (
          <p className='!text-[#af231c] text-sm'>Please select start and finish times.</p>
        )}

        <label className='flex items-center gap-2 cursor-pointer select-none'>
          <input
            type='checkbox'
            id='flexible-time'
            checked={isFlexibleOnTime}
            onChange={({ target }) => setIsFlexibleOnTime(target.checked)}
            className='w-4 h-4 cursor-pointer accent-black shrink-0'
          />
          <span className='text-base text-black font-normal'>Flexible time</span>
        </label>

        {dates[0] && dates[1] && startTime && endTime && (
          <TimingsCaption dates={dates} startTime={startTime} endTime={endTime} />
        )}

        {(isFlexibleOnDate || isFlexibleOnTime) && (
          <div className='flex flex-col gap-2'>
            <label htmlFor='flexibility-notes' className='text-sm sm:text-base text-[#1f1f1f]'>
              Flexibility details:
            </label>
            <input
              id='flexibility-notes'
              type='text'
              value={flexibilityNotes}
              onChange={({ target }) => setFlexibilityNotes(target.value)}
              placeholder='Let us know more...'
              className={`w-full bg-white border border-[#bfbfbf] rounded-lg px-[10px]
                py-2 text-sm sm:text-base text-[#1f1f1f] leading-6 outline-none
                focus:border-[#595959] transition-colors`}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TimingsSection
